/**
 * WarehouseFormShared — hergebruikbare sub-componenten + helpers
 * voor Magazijn-formulieren (registreren én bewerken).
 *
 * Exporteert:
 *   - constants:  SERVICE_TYPES, PACKAGING_TYPES, MEASUREMENT_LABELS, CURRENCIES
 *   - helpers:    num, fmtNum, newColli, calcItemCbm, calcItemVolWeight,
 *                 computeTotals, blockStatus, EMPTY_PARTY, stripParty,
 *                 customerToParty, customerDisplayName
 *   - components: Field, Block, Stat, LiveSummary, ColliRow, PartyFields,
 *                 CustomerSearchField, WarningBanner
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CheckCircle2, Trash2, Box, AlertCircle, Search, Loader2, Layers, Receipt,
} from "lucide-react";

import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import EnterpriseEntityPicker from "../shared/EnterpriseEntityPicker";
import CustomerAutofillPicker from "../../shared/CustomerAutofillPicker";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";
import { adminApi } from "../../../context/AuthContext";

// ============================================================
// Constants
// ============================================================

export const SERVICE_TYPES = [
  { key: "airfreight",    label: "Luchtvracht" },
  { key: "seafreight",    label: "Zeevracht" },
  { key: "ground",        label: "Ground / Wegtransport" },
  { key: "courier",       label: "Courier" },
  { key: "shopdrop_ship", label: "ShopDrop&Ship" },
  { key: "counter_sale",  label: "Balieverkoop" },
];

export const PACKAGING_TYPES = [
  { key: "box",      label: "Doos" },
  { key: "barrel",   label: "Barrel" },
  { key: "suitcase", label: "Koffer" },
  { key: "pallet",   label: "Pallet" },
  { key: "bag",      label: "Zak" },
  { key: "loose",    label: "Los item" },
  { key: "other",    label: "Anders" },
];

export const MEASUREMENT_LABELS = {
  maritimo_cbm: "Maritiem (CBM)",
  maritimo_cf:  "Maritiem (cu.ft)",
  aero_kl:      "Aero (kg)",
  aero_lb:      "Aero (lb)",
  kg_cbm:       "Vracht (kg / CBM)",
  lb_cf:        "Vracht (lb / cu.ft)",
};

// Sprint 3b — 6 Mrt 2026: XCG vervangt ANG voor nieuwe warehouse-pakketten.
// De volledige set komt uit /lib/currencies zodat de app één bron van waarheid heeft.
import { SUPPORTED_CURRENCIES } from "../../../lib/currencies";
export const CURRENCIES = SUPPORTED_CURRENCIES;

/* ============================================================
   Canonieke volumetrische berekening — sprint jun 2026
   ------------------------------------------------------------
   Root cause van de foutieve volumetrische gewichten: hier (en in
   WarehousePackageForm) stond een harde `AIR_DIVISOR = 5000` voor élke
   dienst, terwijl de offerte-/calculatorflow de IATA-deler 6000 gebruikt
   ten onrechte naast andere delers. Nu dezelfde vaste /6000-regel voor
   alle diensten; publieke calculator en formulieren delen de utility.
   De backend blijft authoritative; transportmode/facturatiebasis verandert niet.
   ============================================================ */
import { VOLUMETRIC_DIVISOR, dimensionMetrics, aggregateMeasurements } from "../../../utils/shipmentMeasurements";
export const VOLUMETRIC_DIVISORS = Object.fromEntries(
  ["air", "courier", "sea", "ground", "default"].map((mode) => [mode, VOLUMETRIC_DIVISOR])
);

export const SERVICE_TYPE_TO_TRANSPORT_MODE = {
  airfreight: "air",
  air: "air",
  luchtvracht: "air",
  seafreight: "sea",
  sea: "sea",
  zeevracht: "sea",
  maritimo_cbm: "sea",
  courier: "courier",
  express: "courier",
  shopdrop_ship: "courier",
  shopdrop_ship_air: "air",
  shopdrop_ship_sea: "sea",
  ground: "ground",
  road: "ground",
  wegtransport: "ground",
};

export const TRANSPORT_MODE_LABELS = {
  air: "lucht",
  courier: "courier",
  sea: "zee",
  ground: "weg",
  default: "standaard",
};

export const transportModeForService = (serviceType) =>
  SERVICE_TYPE_TO_TRANSPORT_MODE[String(serviceType || "").trim().toLowerCase()] || "default";

export const volumetricDivisorForService = () => VOLUMETRIC_DIVISOR;

export const EMPTY_PARTY = {
  name: "", company: "", phone: "", whatsapp: "", email: "",
  street: "", house_number: "", house_number_addition: "",
  postal_code: "", city: "", country: "",
};

// ============================================================
// Helpers
// ============================================================

export const num = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = parseFloat(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

export const fmtNum = (n, digits = 2) =>
  Number.isFinite(n) ? n.toFixed(digits).replace(".", ",") : "0,00";

export const newColli = (idx) => ({
  _local_id: `c-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  colli_number: idx + 1,
  description: "",
  quantity: 1,
  weight_kg: "",
  length_cm: "",
  width_cm: "",
  height_cm: "",
  declared_value: "",
  packaging_type: "box",
});

export const calcItemCbm = (it) => {
  const qty = Math.max(num(it.quantity) || 1, 1);
  return dimensionMetrics({ length: it.length_cm, width: it.width_cm, height: it.height_cm, quantity: qty }).cbm;
};

export const calcItemVolWeight = (it, serviceType) => {
  const qty = Math.max(num(it.quantity) || 1, 1);
  return dimensionMetrics({ length: it.length_cm, width: it.width_cm, height: it.height_cm, quantity: qty }).volumetric;
};

export const computeTotals = (items, serviceType) => {
  const totals = aggregateMeasurements((items || []).map((it) => ({
    length: it.length_cm, width: it.width_cm, height: it.height_cm,
    quantity: Math.max(num(it.quantity) || 1, 1), weightPerPiece: it.weight_kg,
  })));
  const { pieces, weight, cbm, volumetric: vw, chargeable } = totals;
  let value = 0;
  (items || []).forEach((it) => {
    const qty = Math.max(num(it.quantity) || 1, 1);
    value += num(it.declared_value) * qty;
  });
  return {
    pieces, weight, cbm, vw, value,
    chargeable,
    chargeableBasis: vw > weight ? "volumetric_weight" : "gross_weight",
    transportMode: transportModeForService(serviceType),
    divisor: volumetricDivisorForService(serviceType),
  };
};

export const blockStatus = (form) => {
  const basisDone = !!(form.warehouse_hub || "").trim() && !!form.service_type;
  const senderDone = !!(form.sender.name || "").trim() &&
    !!((form.sender.phone || "").trim() || (form.sender.whatsapp || "").trim() || (form.sender.email || "").trim());
  const recipientDone = !!(form.recipient.name || "").trim();
  const contentDone = (form.items || []).length > 0 &&
    form.items.every((it) =>
      num(it.weight_kg) > 0 && num(it.length_cm) > 0 && num(it.width_cm) > 0 && num(it.height_cm) > 0,
    );
  return { basisDone, senderDone, recipientDone, contentDone };
};

export const stripParty = (p) => {
  const out = {};
  Object.entries(p || {}).forEach(([k, v]) => {
    const s = typeof v === "string" ? v.trim() : v;
    if (s !== "" && s !== null && s !== undefined) out[k] = s;
  });
  return out;
};

export const isItemEmpty = (it) =>
  !it?.description?.trim() && !it?.weight_kg && !it?.length_cm && !it?.width_cm && !it?.height_cm && !it?.declared_value;

export const customerDisplayName = (c) =>
  c.display_name
    || [c.first_name, c.last_name].filter(Boolean).join(" ").trim()
    || c.company_name
    || c.email
    || "Onbekende klant";

/**
 * Zet een customer-record om naar een Party-object dat past in het
 * sender/recipient veld van een warehouse-shipment.
 *
 * P0 SPRINT (Aug 2026) — CANONICAL CUSTOMER AUTOFILL:
 * gedelegeerd naar `services/customerAutofillService.customerToParty()`.
 * Eén canonical mapping — geen module-lokale legacy field-mapping meer.
 */
export { customerToParty } from "../../../services/customerAutofillService";

// ============================================================
// Components
// ============================================================

export function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase font-bold tracking-wide text-gray-600">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export function Stat({ label, value, sub, accent = "default", testId }) {
  const accentMap = {
    default: "border-gray-200 bg-gray-50 text-[#082c50]",
    blue:    "border-blue-200 bg-blue-50 text-blue-900",
    green:   "border-emerald-200 bg-emerald-50 text-emerald-900",
    purple:  "border-purple-200 bg-purple-50 text-purple-900",
    amber:   "border-amber-200 bg-amber-50 text-amber-900",
  };
  return (
    <div className={`border ${accentMap[accent]} px-3 py-2 rounded-md`} data-testid={testId}>
      <div className="text-[9px] uppercase font-bold tracking-wide opacity-70">{label}</div>
      <div className="font-chivo font-black text-lg leading-tight">{value}</div>
      <div className="text-[10px] opacity-60">{sub}</div>
    </div>
  );
}

export function LiveSummary({ completed, totals, currency, allDone, testIdPrefix = "warehouse-live" }) {
  const pct = Math.round((completed / 4) * 100);
  return (
    <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Stat label="Voortgang" value={`${pct}%`} sub={`${completed}/4 blokken`}
              accent={allDone ? "green" : "blue"} testId={`${testIdPrefix}-progress`} />
        <Stat label="Totaal colli" value={totals.pieces > 0 ? String(totals.pieces) : "—"}
              sub="alle regels" testId={`${testIdPrefix}-pieces`} />
        <Stat label="Totaal gewicht" value={totals.weight > 0 ? `${fmtNum(totals.weight, 2)} kg` : "—"}
              sub="bruto" testId={`${testIdPrefix}-weight`} />
        <Stat label="Totaal CBM" value={totals.cbm > 0 ? fmtNum(totals.cbm, 4) : "—"}
              sub="m³" accent="purple" testId={`${testIdPrefix}-cbm`} />
        <Stat label="Vol. gewicht" value={totals.vw > 0 ? `${fmtNum(totals.vw, 2)}` : "—"}
              sub={`kg (${TRANSPORT_MODE_LABELS[totals.transportMode || "default"]} · /${totals.divisor || VOLUMETRIC_DIVISORS.default})`}
              accent="amber" testId={`${testIdPrefix}-vol-weight`} />
      </div>
      {totals.chargeable > 0 && (
        <div className="mt-2 text-[11px] text-gray-500 font-mono" data-testid={`${testIdPrefix}-chargeable`}>
          Belastbaar gewicht: <span className="text-[#082c50] font-bold">{fmtNum(totals.chargeable, 2)} kg</span>
          {" "}({totals.chargeableBasis === "volumetric_weight" ? "volumetrisch" : "werkelijk"} is leidend)
        </div>
      )}
      {totals.value > 0 && (
        <div className="mt-2 text-[11px] text-gray-500 font-mono">
          Aangegeven waarde totaal: <span className="text-[#082c50] font-bold">{currency} {fmtNum(totals.value, 2)}</span>
        </div>
      )}
    </div>
  );
}

export function Block({ index, title, description, icon: Icon, done, children, testId }) {
  return (
    <section
      className={`bg-white border ${done ? "border-emerald-300 shadow-[0_0_0_1px_rgba(16,185,129,0.15)]" : "border-gray-200"} rounded-md overflow-hidden transition-colors`}
      data-testid={testId}
      data-done={done ? "true" : "false"}
    >
      <header className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-100">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 ${done ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-gray-300 text-gray-500"}`}>
          {done ? <CheckCircle2 className="w-5 h-5" /> : <span className="font-chivo font-black text-sm">{index}</span>}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-[#082c50]" />
            <h3 className="font-chivo font-bold text-sm text-[#082c50]">{title}</h3>
          </div>
          <p className="text-[11px] text-gray-500 leading-tight">{description}</p>
        </div>
        {done && (
          <span className="hidden sm:inline-flex items-center gap-1 text-[10px] text-emerald-700 font-bold uppercase tracking-wide"
                data-testid={`${testId}-done-badge`}>
            <CheckCircle2 className="w-3 h-3" /> Compleet
          </span>
        )}
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

export function ColliRow({ item, index, onChange, onDelete, showDelete, disabled = false, serviceType }) {
  const cbm = useMemo(() => calcItemCbm(item), [item]);
  const volW = useMemo(() => calcItemVolWeight(item, serviceType), [item, serviceType]);
  const filled = num(item.weight_kg) > 0 && num(item.length_cm) > 0 && num(item.width_cm) > 0 && num(item.height_cm) > 0;
  return (
    <div className={`border ${filled ? "border-emerald-200" : "border-gray-200"} bg-white rounded-md p-3`}
         data-testid={`warehouse-colli-row-${index}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-bold ${filled ? "bg-emerald-500 text-white" : "bg-gray-200 text-gray-700"}`}>
            {index + 1}
          </span>
          <span className="font-chivo font-bold text-sm text-[#082c50]">Colli {index + 1}</span>
          {filled && <span className="text-[10px] text-emerald-700 font-bold uppercase tracking-wide">Ingevuld</span>}
        </div>
        {showDelete && !disabled && (
          <button type="button" onClick={onDelete}
                  className="text-gray-400 hover:text-red-600 p-1"
                  title="Verwijder deze colli"
                  data-testid={`warehouse-colli-delete-${index}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
        <div className="sm:col-span-5">
          <Field label="Omschrijving">
            <Input value={item.description || ""}
                   onChange={(e) => onChange({ description: e.target.value })}
                   placeholder="Bv. doos kleding"
                   className="rounded-none h-9 text-xs"
                   disabled={disabled}
                   data-testid={`warehouse-colli-${index}-description`} />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Aantal">
            <Input type="number" inputMode="numeric" min={1}
                   value={item.quantity}
                   onChange={(e) => onChange({ quantity: e.target.value })}
                   className="rounded-none h-9 text-xs"
                   disabled={disabled}
                   data-testid={`warehouse-colli-${index}-quantity`} />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Gewicht (kg) *">
            <Input type="number" inputMode="decimal" step="0.01" min={0}
                   value={item.weight_kg}
                   onChange={(e) => onChange({ weight_kg: e.target.value })}
                   className="rounded-none h-9 text-xs"
                   disabled={disabled}
                   data-testid={`warehouse-colli-${index}-weight`} />
          </Field>
        </div>
        <div className="sm:col-span-3">
          <Field label="Verpakkingstype">
            <Select value={item.packaging_type || "box"}
                    onValueChange={(v) => onChange({ packaging_type: v })}
                    disabled={disabled}>
              <SelectTrigger className="rounded-none h-9 text-xs" data-testid={`warehouse-colli-${index}-packaging`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PACKAGING_TYPES.map((p) => (
                  <SelectItem key={p.key} value={p.key} data-testid={`warehouse-colli-${index}-pack-${p.key}`}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
        <Field label="Lengte (cm) *">
          <Input type="number" inputMode="decimal" step="0.1" min={0}
                 value={item.length_cm}
                 onChange={(e) => onChange({ length_cm: e.target.value })}
                 className="rounded-none h-9 text-xs"
                 disabled={disabled}
                 data-testid={`warehouse-colli-${index}-length`} />
        </Field>
        <Field label="Breedte (cm) *">
          <Input type="number" inputMode="decimal" step="0.1" min={0}
                 value={item.width_cm}
                 onChange={(e) => onChange({ width_cm: e.target.value })}
                 className="rounded-none h-9 text-xs"
                 disabled={disabled}
                 data-testid={`warehouse-colli-${index}-width`} />
        </Field>
        <Field label="Hoogte (cm) *">
          <Input type="number" inputMode="decimal" step="0.1" min={0}
                 value={item.height_cm}
                 onChange={(e) => onChange({ height_cm: e.target.value })}
                 className="rounded-none h-9 text-xs"
                 disabled={disabled}
                 data-testid={`warehouse-colli-${index}-height`} />
        </Field>
        <Field label="Waarde / stuk">
          <Input type="number" inputMode="decimal" step="0.01" min={0}
                 value={item.declared_value}
                 onChange={(e) => onChange({ declared_value: e.target.value })}
                 placeholder="0,00"
                 className="rounded-none h-9 text-xs"
                 disabled={disabled}
                 data-testid={`warehouse-colli-${index}-value`} />
        </Field>
      </div>
      <div className="mt-2 flex items-center gap-3 text-[10px] text-gray-500 font-mono"
           data-testid={`warehouse-colli-${index}-live`}>
        <span>CBM: <span className="text-[#082c50] font-bold">{fmtNum(cbm, 4)} m³</span></span>
        <span>·</span>
        <span>Vol. gewicht: <span className="text-[#082c50] font-bold">{fmtNum(volW, 2)} kg</span></span>
      </div>
    </div>
  );
}

export function PartyFields({ party, onChange, testIdPrefix, nameRequired = false, disabled = false, disabledCountryCity = false }) {
  const set = (k, v) => onChange({ [k]: v });
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={`Naam ${nameRequired ? "*" : ""}`}>
          <Input value={party.name || ""} onChange={(e) => set("name", e.target.value)}
                 disabled={disabled} className="rounded-none h-10"
                 data-testid={`${testIdPrefix}-name`} />
        </Field>
        <Field label="Bedrijfsnaam">
          <Input value={party.company || ""} onChange={(e) => set("company", e.target.value)}
                 disabled={disabled} className="rounded-none h-10"
                 data-testid={`${testIdPrefix}-company`} />
        </Field>
        <Field label="Telefoon">
          <Input value={party.phone || ""} onChange={(e) => set("phone", e.target.value)}
                 disabled={disabled} placeholder="+31..." className="rounded-none h-10"
                 data-testid={`${testIdPrefix}-phone`} />
        </Field>
        <Field label="WhatsApp">
          <Input value={party.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)}
                 disabled={disabled} placeholder="+31..." className="rounded-none h-10"
                 data-testid={`${testIdPrefix}-whatsapp`} />
        </Field>
        <Field label="E-mail">
          <Input type="email" value={party.email || ""} onChange={(e) => set("email", e.target.value)}
                 disabled={disabled} className="rounded-none h-10"
                 data-testid={`${testIdPrefix}-email`} />
        </Field>
        <Field label={`Land ${disabledCountryCity ? "(geen rechten)" : ""}`}>
          <Input value={party.country || ""} onChange={(e) => set("country", e.target.value)}
                 disabled={disabled || disabledCountryCity}
                 className="rounded-none h-10"
                 data-testid={`${testIdPrefix}-country`} />
        </Field>
      </div>
      <div className="grid grid-cols-12 gap-3 mt-3">
        <div className="col-span-12 sm:col-span-6">
          <Field label="Straat">
            <Input value={party.street || ""} onChange={(e) => set("street", e.target.value)}
                   disabled={disabled} className="rounded-none h-10"
                   data-testid={`${testIdPrefix}-street`} />
          </Field>
        </div>
        <div className="col-span-6 sm:col-span-2">
          <Field label="Huisnr.">
            <Input value={party.house_number || ""} onChange={(e) => set("house_number", e.target.value)}
                   disabled={disabled} className="rounded-none h-10"
                   data-testid={`${testIdPrefix}-house_number`} />
          </Field>
        </div>
        <div className="col-span-6 sm:col-span-4">
          <Field label="Toev.">
            <Input value={party.house_number_addition || ""} onChange={(e) => set("house_number_addition", e.target.value)}
                   disabled={disabled} className="rounded-none h-10"
                   data-testid={`${testIdPrefix}-house_number_addition`} />
          </Field>
        </div>
        <div className="col-span-6 sm:col-span-3">
          <Field label="Postcode">
            <Input value={party.postal_code || ""} onChange={(e) => set("postal_code", e.target.value)}
                   disabled={disabled} className="rounded-none h-10"
                   data-testid={`${testIdPrefix}-postal_code`} />
          </Field>
        </div>
        <div className="col-span-6 sm:col-span-9">
          <Field label={`Plaats ${disabledCountryCity ? "(geen rechten)" : ""}`}>
            <Input value={party.city || ""} onChange={(e) => set("city", e.target.value)}
                   disabled={disabled || disabledCountryCity}
                   className="rounded-none h-10"
                   data-testid={`${testIdPrefix}-city`} />
          </Field>
        </div>
      </div>
    </>
  );
}

export function CustomerSearchField({ onSelect, testIdPrefix }) {
  // P0 SPRINT (Aug 2026) — CANONICAL CUSTOMER AUTOFILL PICKER.
  // Alle modules gebruiken deze picker; legacy `customerToParty()` uit deze
  // file is nu een re-export van de canonical service (single source of truth).
  return (
    <CustomerAutofillPicker
      label="Bestaande klant zoeken (optioneel)"
      onSelect={onSelect}
      testIdPrefix={testIdPrefix || "wh-customer"}
      autoFocus={false}
      showRecent={false}
    />
  );
}

export function WarningBanner({ tone = "amber", icon: Icon = AlertCircle, title, children, testId }) {
  const toneMap = {
    red:    "bg-red-50 border-red-200 text-red-900 [&_svg]:text-red-600",
    amber:  "bg-amber-50 border-amber-200 text-amber-900 [&_svg]:text-amber-600",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-900 [&_svg]:text-indigo-600",
  };
  return (
    <div className={`border px-3 py-2 rounded-md ${toneMap[tone]}`} data-testid={testId}>
      <div className="flex items-start gap-2">
        <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div className="text-xs">
          {title && <div className="font-bold mb-0.5">{title}</div>}
          <div>{children}</div>
        </div>
      </div>
    </div>
  );
}

// Helper voor frontend mapping van API-items → form-state
export function itemsFromApi(items) {
  if (!Array.isArray(items) || items.length === 0) return [newColli(0)];
  return items.map((it, idx) => ({
    _local_id: it.id || `c-api-${idx}-${Math.random().toString(36).slice(2, 7)}`,
    id: it.id,
    colli_number: it.colli_number || idx + 1,
    description: it.description || "",
    quantity: it.quantity ?? 1,
    weight_kg: it.weight_kg ?? "",
    length_cm: it.length_cm ?? "",
    width_cm: it.width_cm ?? "",
    height_cm: it.height_cm ?? "",
    declared_value: it.declared_value ?? "",
    packaging_type: it.packaging_type || "box",
  }));
}

// Re-export icons we use commonly
export { Box };
